/**
 * PERF-06 (audit 26-06-09, I-6) — prefetch the per-image OVERLAY DATA
 * (findings + defects) alongside the image bytes the P1 window already
 * warms, so stepping to a prefetched image paints its bounding boxes from
 * cache instead of waiting an RTT.
 *
 * Key discipline (the load-bearing part): the prefetch builds its query
 * keys + fetchers through the SAME builders the mounted hooks use —
 * `effectiveFindingSetParams` + `buildFindingsListQuery` for the Viewer's
 * effective finding set, `imageScopedDefectsParams` +
 * `buildDefectsListQuery` for the defects overlay. Key drift = cache miss
 * = wasted work, so there is deliberately no second key-construction path.
 *
 * Throttle: only the next OVERLAY_PREFETCH_AHEAD images in the CURRENT
 * filtered order (overlay payloads are small; the win saturates quickly —
 * I-6 says 3–5). `staleTime` (shared N-5 constant) stops refires for
 * entries that are already warm, and the whole effect is skipped while the
 * filtered set is `outOfSync` — a filter change is about to invalidate
 * exactly these queries, so prefetching them would be wasted bytes.
 * Invalidation correctness is free: mutations already invalidate by key
 * prefix, which covers prefetched entries too.
 */
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import { buildFindingsListQuery } from "../../findings/queries.js";
import {
  buildDefectsListQuery,
  imageScopedDefectsParams,
} from "../../defects/queries.js";
import { effectiveFindingSetParams } from "../useEffectiveFindingSet.js";
import { useEffectiveAiClassFilter } from "../useEffectiveAiClassFilter.js";
import { OVERLAY_STALE_TIME_MS } from "../overlayQueryPolicy.js";

/** How many images AHEAD of the active one get their overlay data warmed. */
export const OVERLAY_PREFETCH_AHEAD = 4;

export interface OverlayPrefetchInput {
  dataAcquisitionId: string;
  /**
   * The filtered-order image list (the order the user navigates) — the
   * same `ordered` the image-byte scheduler windows over in `usePrefetch`.
   */
  ordered: readonly { imageId: string }[];
  /** Active image id (P0); the overlay window starts right after it. */
  activeImageId: string | null;
  /**
   * True while the filtered data-point set does not answer the latest
   * filter revision — a filter change is about to invalidate the overlay
   * queries, so prefetching is suspended (I-6).
   */
  outOfSync: boolean;
}

export function useOverlayPrefetch({
  dataAcquisitionId,
  ordered,
  activeImageId,
  outOfSync,
}: OverlayPrefetchInput): void {
  const qc = useQueryClient();
  const api = useApi();
  // The committed AI-class filter — the SAME hook the mounted effective-
  // finding-set reads, at the same instant, so the prefetch key carries the
  // identical narrowing.
  const aiClassFilter = useEffectiveAiClassFilter();

  useEffect(() => {
    if (!dataAcquisitionId || outOfSync || ordered.length === 0) return;

    const activeIdx = activeImageId
      ? ordered.findIndex((i) => i.imageId === activeImageId)
      : -1;
    // Same fallback as the image window: a freshly-opened dataset (no
    // activation yet, or the active image filtered out) warms from the
    // front of the filtered order.
    const center = activeIdx >= 0 ? activeIdx : 0;

    for (let d = 1; d <= OVERLAY_PREFETCH_AHEAD; d += 1) {
      const next = ordered[center + d];
      if (!next) break;
      void qc.prefetchQuery({
        ...buildFindingsListQuery(
          api,
          dataAcquisitionId,
          effectiveFindingSetParams({ imageId: next.imageId, aiClassFilter }),
        ),
        staleTime: OVERLAY_STALE_TIME_MS,
      });
      void qc.prefetchQuery({
        ...buildDefectsListQuery(
          api,
          dataAcquisitionId,
          imageScopedDefectsParams(next.imageId),
        ),
        staleTime: OVERLAY_STALE_TIME_MS,
      });
    }
  }, [
    dataAcquisitionId,
    ordered,
    activeImageId,
    outOfSync,
    aiClassFilter,
    qc,
    api,
  ]);
}
